import { useQueryClient } from '@tanstack/react-query'
import { SYSTEM_TYPE } from '@/app/store/systemStore'
import { useOmnicommStore } from '@/app/store/omnicommStore'
import { useAxentaStore } from '@/app/store/axentaStore'
import { useGeliosStore } from '@/app/store/geliosStore'
import { useFortMonitorStore } from '@/app/store/fortmonitorStore'
import { useGlonassoftStore } from '@/app/store/glonassoftStore'
import { useAuthStore } from '@/app/store/authStore'
import { getSourceApi } from '@/shared/api/config'
import {
  agentsQueryOptions,
  AgentsErrorReason,
} from '@/shared/api/useAgentsQuery'
import {
  OMNICOMM_DEFAULT_URL,
  AXENTA_DEFAULT_URL,
  GELIOS_DEFAULT_URL,
  FORTMONITOR_DEFAULT_URL,
  GLONASSOFT_DEFAULT_URL,
} from '@/shared/config/systemUrls'

export type AuthableSystemType =
  | SYSTEM_TYPE.OMNICOMM
  | SYSTEM_TYPE.AXENTA
  | SYSTEM_TYPE.GELIOS
  | SYSTEM_TYPE.FORTMONITOR
  | SYSTEM_TYPE.GLONASSOFT

interface SystemCredentials {
  login: string
  password: string
}

export type VerifyResult = { success: true } | { success: false; error: string }

const REASON_MESSAGES: Record<AgentsErrorReason, string> = {
  invalid_credentials: 'Логин и/или пароль введены неверно',
  no_response: 'Сервис недоступен. Проверьте подключение и попробуйте позже',
  unknown: 'Не удалось подключиться. Попробуйте позже',
}

export const useSystemAuthCheck = () => {
  const queryClient = useQueryClient()
  const omnicommStore = useOmnicommStore()
  const axentaStore = useAxentaStore()
  const geliosStore = useGeliosStore()
  const fortMonitorStore = useFortMonitorStore()
  const glonassoftStore = useGlonassoftStore()
  const { setTokenAccount } = useAuthStore()

  const getSystemUrl = (systemType: AuthableSystemType) => {
    switch (systemType) {
      case SYSTEM_TYPE.OMNICOMM:
        return omnicommStore.server || OMNICOMM_DEFAULT_URL
      case SYSTEM_TYPE.AXENTA:
        return AXENTA_DEFAULT_URL
      case SYSTEM_TYPE.GELIOS:
        return geliosStore.server || GELIOS_DEFAULT_URL
      case SYSTEM_TYPE.FORTMONITOR:
        return fortMonitorStore.server || FORTMONITOR_DEFAULT_URL
      case SYSTEM_TYPE.GLONASSOFT:
        return glonassoftStore.server || GLONASSOFT_DEFAULT_URL
    }
  }

  const saveCredentials = (
    systemType: AuthableSystemType,
    credentials: SystemCredentials
  ) => {
    switch (systemType) {
      case SYSTEM_TYPE.OMNICOMM:
        omnicommStore.setCredentials(credentials)
        break
      case SYSTEM_TYPE.AXENTA:
        axentaStore.setCredentials(credentials)
        break
      case SYSTEM_TYPE.GELIOS:
        geliosStore.setCredentials(credentials)
        break
      case SYSTEM_TYPE.FORTMONITOR:
        fortMonitorStore.setCredentials(credentials)
        break
      case SYSTEM_TYPE.GLONASSOFT:
        glonassoftStore.setCredentials(credentials)
        break
    }
    setTokenAccount(credentials.login)
  }

  const verifyAndSaveCredentials = async (
    systemType: AuthableSystemType,
    credentials: SystemCredentials
  ): Promise<VerifyResult> => {
    try {
      const result = await queryClient.fetchQuery(
        agentsQueryOptions({
          systemType,
          credentials,
          sourceApiUrl: getSourceApi(systemType),
          systemUrl: getSystemUrl(systemType),
        })
      )

      if (result.error) {
        queryClient.removeQueries({
          queryKey: ['agents', systemType, credentials],
          exact: true,
        })
        return {
          success: false,
          error: REASON_MESSAGES[result.reason ?? 'unknown'],
        }
      }
    } catch (error) {
      console.error('❌ Ошибка проверки учётных данных:', error)
      return { success: false, error: REASON_MESSAGES.unknown }
    }

    saveCredentials(systemType, credentials)
    return { success: true }
  }

  return { verifyAndSaveCredentials, saveCredentials }
}
